import { useCallback, useState } from 'react'
import { singleDbUpsert } from '../utils/helpers/supabase.helpers'
import { GEOXP_SPOTS_TABLE } from '../utils/constants/db.constants'
import { useFeatureAuth } from './authorization'
import { FEATURES } from '../utils/constants/features.constants'

interface UseAgamiMediaSignedUrlDeleteParams {
  agamiId: string
  onSuccess?: (data: Record<string, unknown> | Record<string, unknown>[] | null | undefined) => void
  onError?: (error: string | Error | Record<string, unknown> | unknown) => void
}

interface UseAgamiMediaSignedUrlDeleteReturn {
  deleteUrl: () => Promise<void>
  isLoading: boolean
}

export const useAgamiMediaSignedUrlDelete = ({
  agamiId,
  onSuccess,
  onError,
}: UseAgamiMediaSignedUrlDeleteParams): UseAgamiMediaSignedUrlDeleteReturn => {
  const experimentalFeats = useFeatureAuth(FEATURES.EXPERIMENTAL_FEATURES)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const manageError = useCallback(
    (error: string | Error | Record<string, unknown> | unknown) => {
      setIsLoading(false)

      if (onError) {
        onError(error)
      }
    },
    [onError]
  )

  const deleteUrl = useCallback(async (): Promise<void> => {
    try {
      if (!experimentalFeats) {
        manageError('Operation not allowed')
        return
      }

      if (!agamiId) {
        manageError('Invalid agami ID')
        return
      }

      setIsLoading(true)

      const { data, error } = await singleDbUpsert({
        table: GEOXP_SPOTS_TABLE,
        upsertData: {
          id: agamiId,
          audio_signed_url: null,
          audio_signed_url_expires_at: null,
        },
      })

      if (error) {
        manageError(error)
        return
      }

      setIsLoading(false)

      if (onSuccess) {
        onSuccess(data)
      }
    } catch (err) {
      manageError(err)
    }
  }, [experimentalFeats, agamiId, manageError, onSuccess])

  return {
    deleteUrl,
    isLoading,
  }
}
